/* eslint no-prototype-builtins: 0 */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import {
  Button,
  Divider,
  Dropdown,
  Form,
  Grid,
  Icon,
  Modal,
  Menu,
  Segment,
} from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import { SortableContainer, SortableElement, SortableHandle, arrayMove } from 'react-sortable-hoc';
import _ from 'lodash';
import { DEFAULT_BOOKING_FIELD_LIST, PURCHASER_CUSTOM_FIELDS } from '../constants/defaultBookingFields';
import { HIMS_UNIT_FIELDS } from '../constants/hims';
import { VA_UNIT_FIELDS } from '../constants/virtualAccount';
import { namespaces, TRANSLATION_FILES } from '../constants/translation';
import RegexDropdownPicker, { checkRegex } from './RegexDropdownPicker';

const getFieldTypeLabels = (type, isDefault = false) => {
  const defaultOpts = [
    { value: 'dropdown', text: 'Dropdown Single' },
    { value: 'dropdown_multi', text: 'Dropdown Multiple' },
    { value: 'input', text: 'Single Line Text' },
    { value: 'textarea', text: 'Multi Line Text' },
  ];
  if (type === 'unit') {
    if (isDefault) {
      return defaultOpts.filter(o => !o.value.includes('dropdown_multi'));
    }
    return defaultOpts.filter(o => !o.value.includes('dropdown'));
  }
  return defaultOpts;
};

const sortItems = [
  { name: 'alphabetically', text: 'Reorder Alphabetically' },
  { name: 'newest', text: 'Reorder By Newest' },
  { name: 'oldest', text: 'Reorder By Oldest' },
];

const DragHandle = SortableHandle(() => <Icon size="large" disabled name="content" className="DragHandleIcon" />);

const SortableOptionItem = SortableElement(({
  data,
  idx,
  onChange,
  onEdit,
  t,
}) => (
  <Grid className="item">
    <Grid.Row verticalAlign="middle" textAlign="center">
      <Grid.Column mobile={3} tablet={2} computer={2}>
        {data.isEditing ? '' : <DragHandle />}
      </Grid.Column>
      <Grid.Column mobile={10} tablet={12} computer={12}>
        <b>{data.title}</b>
      </Grid.Column>
      <Grid.Column mobile={3} tablet={2} computer={2} textAlign="right">
        <button
          className="a-button-gray"
          onClick={e => onEdit(e, idx)}
        >
          <Icon
            name={data.isEditing ? 'x' : 'edit'}
            size="large"
            color="grey"
          />
        </button>
      </Grid.Column>
    </Grid.Row>
    {data.isEditing ?
      <Grid.Row verticalAlign="middle" className="EditArea">
        <Grid.Column width={16}>
          <Form>
            <Form.Group widths="equal">
              <Form.Input
                fluid
                name="title"
                label={`${t('Choice Name')} (${t('Required').toLowerCase()})`}
                placeholder={`${t('Choice Name')}*`}
                value={data.title}
                onChange={(e, d) => onChange(e, d, idx)}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                fluid
                name="value"
                label={`${t('Choice Value')} (${t('Required').toLowerCase()})`}
                placeholder={`${t('Choice Value')}*`}
                value={data.value}
                onChange={(e, d) => onChange(e, d, idx)}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Field>
                <label htmlFor="dataValue">{t('Data (JSON Format)')}</label>
                <div className="ui fluid input">
                  <textarea
                    classID="dataValue"
                    name="dataValue"
                    className="semantic-textbox"
                    style={{ minHeight: '6em', maxHeight: '40vh', width: '100%' }}
                    value={data.dataValue}
                    onChange={(e, d) => onChange(e, d, idx)}
                  />
                </div>
              </Form.Field>
            </Form.Group>
            {data.id !== '' &&
              <Form.Group widths="equal">
                <Form.Checkbox
                  label={`${t('Delete', { ns: TRANSLATION_FILES.translation })} (${t('erases this {{field}}', { field: t('Option').toLowerCase() })})`}
                  name="isDeleted"
                  checked={data.isDeleted}
                  onChange={(e, d) => onChange(e, d, idx)}
                />
              </Form.Group>
            }
          </Form>
        </Grid.Column>
      </Grid.Row>
    : ''}
  </Grid>
));


const SortableOptionList = SortableContainer(({
  addDisabled,
  fieldID,
  items,
  onAdd,
  onChange,
  onEdit,
  t,
}) => (
  <div className="contain">
    {items.map((data, index) => (
      <SortableOptionItem
        data={data}
        fieldID={fieldID}
        idx={index}
        index={index}
        key={`item-${index}`}
        onChange={onChange}
        onEdit={onEdit}
        t={t}
      />
    ))}
    <Grid className="foot">
      <Grid.Row verticalAlign="middle">
        <Grid.Column textAlign="center">
          <AddOptionButton
            isDisabled={addDisabled}
            onClick={onAdd}
            t={t}
          />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  </div>
));

const AddOptionButton = ({ isDisabled, onClick, t }) => (
  <Button.Group fluid>
    <Button
      disabled={isDisabled}
      onClick={onClick}
      style={{ width: '65%' }}
    >
      {t('Add Choice')}
    </Button>
  </Button.Group>
);
AddOptionButton.propTypes = {
  isDisabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  t: PropTypes.func,
};

const CustomFieldOptionModal = ({
  addDisabled,
  data,
  onAdd,
  onChange,
  onEdit,
  onSave,
  onCancel,
  onSortEnd,
  open,
  saveDisabled,
  t,
}) => (
  <Modal open={open}>
    <Modal.Header>
      Dropdown Choices
      <Button onClick={onCancel} floated="right" color="grey">Cancel</Button>
      <Button onClick={onSave} floated="right" primary disabled={saveDisabled}>Proceed</Button>
    </Modal.Header>
    <Modal.Content>
      <Grid className="PBCFOSetup">
        <Grid.Row>
          <Grid.Column width={16}>
            <SortableOptionList
              addDisabled={addDisabled}
              helperClass="PBCFOSetup isSorting"
              items={data}
              lockAxis="y"
              lockToContainerEdges
              onAdd={onAdd}
              onChange={onChange}
              onEdit={onEdit}
              onSortEnd={onSortEnd}
              useDragHandle
              t={t}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Modal.Content>
  </Modal>
);
CustomFieldOptionModal.propTypes = {
  addDisabled: PropTypes.bool,
  data: PropTypes.oneOfType([PropTypes.array]),
  onAdd: PropTypes.func,
  onChange: PropTypes.func,
  onEdit: PropTypes.func,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  onSortEnd: PropTypes.func,
  open: PropTypes.bool,
  saveDisabled: PropTypes.bool,
  t: PropTypes.func,
};

const SortableFieldItem = SortableElement(({
  data,
  idx,
  onChange,
  onClickName,
  onClickOptions,
  onEdit,
  type,
  t,
}) => {
  const fieldLabel = getFieldTypeLabels(type, data.isDefault).find(d => d.value === data.type);
  let fieldOptions = [{
    text: t('Disabled', { ns: TRANSLATION_FILES.constants }),
    value: '',
  }];
  if (type === 'unit' && (data.type === 'dropdown' || data.type === 'dropdown_multi') &&
    data.projectUnitCustomFieldSettingOptions &&
    data.projectUnitCustomFieldSettingOptions.length > 0
  ) {
    data.projectUnitCustomFieldSettingOptions.sort(
      (a, b) => a.position - b.position).forEach((d) => {
        if (d.title !== '' && d.value !== '') {
          fieldOptions.push(
            { text: d.title, value: d.value, id: d.id },
          );
        }
      },
    );
  }
  if (type === 'booking' && (data.type === 'dropdown' || data.type === 'dropdown_multi') &&
    data.projectBookingCustomFieldOptions &&
    data.projectBookingCustomFieldOptions.length > 0
  ) {
    data.projectBookingCustomFieldOptions.sort(
      (a, b) => a.position - b.position).forEach((d) => {
        if (d.title !== '' && d.value !== '') {
          fieldOptions.push(
            { text: d.title, value: d.value, id: d.id },
          );
        }
      },
    );
  }
  if (data.type === 'dropdown_multi') {
    fieldOptions = fieldOptions.filter(f => f.value !== '');
  }
  const previewOptions = fieldOptions.filter(f => f.value !== '');
  const renderLabel = label => ({
    content: (label && label.text) ? label.text : '',
    circular: true,
  });
  const renderErrorMessage = () => (
    <div style={{ color: 'red', fontSize: '14px', marginTop: '0.4em' }}>{data.errorMessage}</div>
  );
  return (
    <Grid className="item">
      <Grid.Row verticalAlign="middle" textAlign="center">
        <Grid.Column mobile={3} tablet={2} computer={2}>
          {data.isEditing ? '' : <DragHandle />}
        </Grid.Column>
        <Grid.Column mobile={10} tablet={12} computer={12}>
          <span style={(type === 'booking' && data.isDeleted) || (type === 'unit' && data.isHidden) ? { textDecoration: 'line-through' } : {}}>{data.isEditing && !data.isDefault ? <button onClick={e => onClickName(e, idx)} className="a-button-blue">{data.label}</button> : (data.isDefault ? t(data.label, { ns: TRANSLATION_FILES.screens }) : data.label)} <small>({fieldLabel ? t(fieldLabel.text, { ns: TRANSLATION_FILES.constants }) : ''})</small></span>
          {data.isEditing && data.showName ?
            <Form.Input
              name="name"
              placeholder={`${t('Name')}*`}
              value={data.name}
              onChange={(e, d) => onChange(e, d, idx)}
              size="mini"
            />
            : ''
          }
        </Grid.Column>
        <Grid.Column mobile={3} tablet={2} computer={2} textAlign="right">
          <button
            className="a-button-gray"
            onClick={e => onEdit(e, idx)}
          >
            <Icon
              name={data.isEditing ? 'x' : 'edit'}
              size="large"
              color="grey"
            />
          </button>
        </Grid.Column>
      </Grid.Row>
      {data.isEditing ?
        <Grid.Row verticalAlign="middle" className="EditArea">
          <Grid.Column width={16}>
            <Form.Group widths="equal">
              <Form.Select
                fluid
                name="type"
                label={`${t('Field Type')} (${t('Required').toLowerCase()})`}
                value={data.type}
                options={getFieldTypeLabels(type, data.isDefault).map(l => ({
                  ...l,
                  text: t(l.text, { ns: TRANSLATION_FILES.constants }),
                }))}
                onChange={(e, d) => onChange(e, d, idx)}
                disabled={data.isDefault}
              />
              <Form.Input
                fluid
                name="label"
                label={`${t('Field Label')} (${t('Required').toLowerCase()})`}
                placeholder={`${t('Field Label')}*`}
                value={data.label}
                onChange={(e, d) => onChange(e, d, idx)}
                disabled={data.name === 'email' && data.isDefault}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                fluid
                name="placeholder"
                label={t('Placeholder Text')}
                placeholder={t('Placeholder Text')}
                value={data.placeholder}
                onChange={(e, d) => onChange(e, d, idx)}
                disabled={data.name === 'email' && data.isDefault}
              />
              { (data.type === 'dropdown' || data.type === 'dropdown_multi') ?
                <Form.Select
                  fluid
                  name="defaultValue"
                  label={t('Default Value')}
                  placeholder={t('Disabled', { ns: TRANSLATION_FILES.constants })}
                  multiple={data.type === 'dropdown_multi'}
                  value={data.type === 'dropdown_multi' && !_.isArray(data.defaultValue) ? data.defaultValue.split(',') : data.defaultValue}
                  options={fieldOptions}
                  onChange={(e, d) => onChange(e, d, idx)}
                  renderLabel={renderLabel}
                />
              :
                <Form.Input
                  fluid
                  name="defaultValue"
                  label={t('Default Value')}
                  placeholder={t('Default Value')}
                  value={data.defaultValue}
                  onChange={(e, d) => onChange(e, d, idx)}
                  disabled={data.name === 'email' && data.isDefault}
                />
              }
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Checkbox
                label={t('Required')}
                name="isRequired"
                checked={data.isRequired}
                onChange={(e, d) => onChange(e, d, idx)}
                disabled={type === 'unit' && data.isDefault}
              />
              {!data.isDefault &&
                <Form.Checkbox
                  label={t('Lock / Disabled')}
                  name="isDisabled"
                  checked={data.isDisabled}
                  onChange={(e, d) => onChange(e, d, idx)}
                />
              }
              {type === 'booking' && data.id !== '' && !data.isDefault &&
                <Form.Checkbox
                  label={`${t('Delete', { ns: TRANSLATION_FILES.translation })} (${t('erases this {{field}}', { field: t('Field').toLowerCase() })})`}
                  name="isDeleted"
                  checked={data.isDeleted}
                  onChange={(e, d) => onChange(e, d, idx)}
                />
              }
              {((type === 'booking' && data.isDefault) || (type === 'unit' && data.id !== '')) &&
                <Form.Checkbox
                  label={t('Hide')}
                  name="isHidden"
                  checked={data.isHidden}
                  onChange={(e, d) => onChange(e, d, idx)}
                  disabled={data.name === 'email' || (type === 'unit' && data.isDefault)}
                />
              }
            </Form.Group>
            {type === 'booking' && data.isRequired && !data.isDefault &&
              <Form.Group>
                <Form.Field width={8}>
                  <label>{t('Required at status')}</label>
                  <div style={{ fontSize: '12px', color: '#777', marginBottom: '5px' }}>
                    {t('Field becomes required once the unit reaches the status')}
                  </div>
                  <Form.Select
                    fluid
                    name="requiredAtStatus"
                    placeholder={t('Select status')}
                    value={data.requiredAtStatus || 'reserved'}
                    options={[
                      { text: t('Reserved'), value: 'reserved' },
                      { text: t('Booked'), value: 'booked' },
                      { text: t('Sold'), value: 'sold_spa_signed' },
                    ]}
                    onChange={(e, d) => onChange(e, d, idx)}
                  />
                </Form.Field>
              </Form.Group>
            }
          </Grid.Column>
          {!data.isDefault && (data.type === 'dropdown' || data.type === 'dropdown_multi') ?
            <Grid.Column width={16} textAlign="center">
              <Button onClick={e => onClickOptions(e, idx)}>
                {t('Dropdown Choices')}
              </Button>
            </Grid.Column>
          : ''}
          {type === 'booking' && data.type === 'input' &&
            <Grid.Column width={16} textAlign="left">
              <RegexDropdownPicker
                formatValue={data.formatName}
                regexValue={data.regex}
                onChange={(e, d) => onChange(e, d, idx)}
              />
            </Grid.Column>
          }
          <Grid.Column width={16}>
            <Divider hidden />
            <Segment tertiary>
              <center><h3>{t('Live Preview')}</h3></center>
              {data.type === 'textarea' &&
                <Form.Group widths="equal">
                  <Form.TextArea
                    autoComplete="off"
                    disabled={data.isDisabled}
                    label={`${data.isDefault ? t(data.label, { ns: TRANSLATION_FILES.screens }) : data.label}${data.isRequired ? '*' : ''}`}
                    name={data.name}
                    placeholder={data.placeholder}
                    value={data.defaultValue}
                    style={{ minHeight: '6em', height: '6em' }}
                  />
                </Form.Group>
              }
              {data.type === 'input' &&
                <Form.Group widths="equal">
                  <Form.Input
                    autoComplete="off"
                    disabled={data.isDisabled}
                    label={`${data.isDefault ? t(data.label, { ns: TRANSLATION_FILES.screens }) : data.label}${data.isRequired ? '*' : ''}`}
                    name={data.name}
                    placeholder={data.placeholder}
                    value={data.defaultValue}
                    error={!!(data.errorMessage) && renderErrorMessage}
                  />
                </Form.Group>
              }
              {(data.type === 'dropdown' || data.type === 'dropdown_multi') &&
                <Form.Group widths="equal">
                  <Form.Select
                    className="custom_dropdown"
                    disabled={data.isDisabled}
                    label={`${data.isDefault ? t(data.label, { ns: TRANSLATION_FILES.screens }) : data.label}${data.isRequired ? '*' : ''}`}
                    name={data.name}
                    options={previewOptions}
                    placeholder={data.placeholder}
                    search
                    selectOnBlur={false}
                    value={data.type === 'dropdown_multi' && !_.isArray(data.defaultValue) ? data.defaultValue.split(',') : data.defaultValue}
                    multiple={data.type === 'dropdown_multi'}
                    renderLabel={renderLabel}
                  />
                </Form.Group>
              }
            </Segment>
          </Grid.Column>
        </Grid.Row>
      : ''}
    </Grid>
  );
});

const SortableFieldList = SortableContainer(({
  addDisabled,
  items,
  onAdd,
  onAddChange,
  onChange,
  onClickOptions,
  onClickName,
  onEdit,
  isDefault = false,
  type,
  t,
  startIndex = 0,
}) => (
  <div className="contain">
    {items.map((data, idx) => {
      const index = idx + startIndex;
      return (
        <SortableFieldItem
          data={data}
          idx={index}
          index={index}
          key={`item-${index}`}
          onChange={onChange}
          onEdit={onEdit}
          onClickOptions={onClickOptions}
          onClickName={onClickName}
          type={type}
          t={t}
        />
      );
    })}
    {!isDefault &&
      <Grid className="foot">
        <Grid.Row verticalAlign="middle">
          <Grid.Column textAlign="center">
            <AddFieldButton
              isDisabled={addDisabled}
              onChange={onAddChange}
              onClick={onAdd}
              type={type}
              t={t}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    }
  </div>
));

const AddFieldButton = ({
  isDisabled,
  onChange,
  onClick,
  type,
  t,
}) => (
  <Button.Group fluid>
    <Dropdown
      button
      className="icon teal"
      defaultValue="input"
      disabled={isDisabled}
      onChange={onChange}
      options={getFieldTypeLabels(type).map(l => ({
        ...l,
        text: t(l.text, { ns: TRANSLATION_FILES.constants }),
      }))}
      noResultsMessage={t('No results found', { ns: TRANSLATION_FILES.translation })}
    />
    <Button
      color="grey"
      disabled={isDisabled}
      onClick={onClick}
      style={{ width: '65%' }}
    >
      {t('Add Field')}
    </Button>
  </Button.Group>
);
AddFieldButton.propTypes = {
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  type: PropTypes.string,
  t: PropTypes.func,
};

class CustomField extends Component {
  static propTypes = {
    data: PropTypes.oneOfType([PropTypes.array]).isRequired,
    onChangeData: PropTypes.func,
    type: PropTypes.string,
    auth: PropTypes.shape({
      company: PropTypes.shape({
        countryCode: PropTypes.string,
      }),
    }),
    appConfig: PropTypes.shape({
      data: PropTypes.shape({
        himsIsEnabled: PropTypes.bool,
        settings: PropTypes.shape({
          virtualAccountIsEnabled: PropTypes.bool,
        }),
      }),
    }),
    t: PropTypes.func,
  }
  static defaultBookingValues = {
    id: '',
    type: 'input',
    name: '',
    label: '',
    placeholder: '',
    defaultValue: '',
    className: '',
    style: '',
    isRequired: false,
    requiredAtStatus: 'reserved',
    isDisabled: false,
    isDeleted: false,
    isEditing: true,
    position: 1,
    projectBookingCustomFieldOptions: [],
    showName: false,
    isDefault: false,
    isHidden: false,
    formatName: 'none',
    regex: null,
    errorMessage: null,
  };
  static defaultUnitValues = {
    id: '',
    type: 'input',
    name: '',
    label: '',
    placeholder: '',
    defaultValue: '',
    className: '',
    style: '',
    isRequired: false,
    isDisabled: false,
    isHidden: false,
    isEditing: true,
    position: 1,
    projectUnitCustomFieldSettingOptions: [],
    showName: false,
  };
  static defaultBookingOptionValues = {
    id: '',
    projectBookingCustomFieldID: '',
    title: '',
    value: '',
    isDeleted: false,
    isEditing: true,
    dataValue: '',
    position: 1,
  };
  static defaultUnitOptionValues = {
    id: '',
    projectUnitCustomFieldSettingID: '',
    title: '',
    value: '',
    isDeleted: false,
    isEditing: true,
    dataValue: '',
    position: 1,
  };
  static mapStateToProps = state => ({
    auth: state.auth,
    appConfig: state.appConfig,
  })
  static getPropsToState = (props) => {
    const data = props.data || [];
    data.forEach((d, idx) => {
      if (!data[idx].hasOwnProperty('isEditing')) {
        data[idx].isEditing = false;
      }
      if (props.type === 'unit' && d.type === 'dropdown') {
        const newOptions = d.projectUnitCustomFieldSettingOptions;
        if (newOptions) {
          d.projectUnitCustomFieldSettingOptions.forEach((o, id) => {
            if (!newOptions[id].hasOwnProperty('isEditing')) {
              newOptions[id].isEditing = false;
            }
          });
        }
        data[idx].projectBookingCustomFieldOptions = newOptions;
      } else if (d.type === 'dropdown') {
        const newOptions = d.projectBookingCustomFieldOptions;
        if (newOptions) {
          d.projectBookingCustomFieldOptions.forEach((o, id) => {
            if (!newOptions[id].hasOwnProperty('isEditing')) {
              newOptions[id].isEditing = false;
            }
          });
        }
        data[idx].projectBookingCustomFieldOptions = newOptions;
      }
    });
    const d = data.filter(c => !c.isDefault);
    let defaultData = data.filter(c => c.isDefault);
    if (props.type === 'booking') {
      if (defaultData.length === 0) {
        defaultData = DEFAULT_BOOKING_FIELD_LIST;
      }
      // check for new default booking field (email)
      const emailIdx = defaultData.findIndex(dd => dd.name === 'email');
      if (emailIdx === -1) {
        defaultData = defaultData.concat(PURCHASER_CUSTOM_FIELDS);
      }
    }
    return {
      data: d,
      defaultData,
      editingData: JSON.parse(JSON.stringify(d)),
    };
  }
  constructor(props) {
    super(props);
    const state = CustomField.getPropsToState(props);
    this.state = {
      ...state,
      optionFieldID: 0,
      optionModalIsOpen: false,
      addType: 'input',
      isDefault: false,
    };
  }
  componentWillReceiveProps(nextProps) {
    const state = CustomField.getPropsToState(nextProps);
    if (JSON.stringify(this.state.data) !== JSON.stringify(state.data) ||
      JSON.stringify(this.state.defaultData) !== JSON.stringify(state.defaultData)) {
      this.setState({ ...state });
    }
  }
  onSortEnd = (value, isDefault = false) => {
    const { data, defaultData } = this.state;
    const newData = isDefault ? defaultData : data;
    this.setState({ isDefault }, () => this.sortDataByIndex(arrayMove(newData, value.oldIndex, value.newIndex)));
  };
  onSortOptionsEnd = ({ oldIndex, newIndex }) => {
    const { type } = this.props;
    const { editingData, optionFieldID } = this.state;
    if (type === 'unit') {
      editingData[optionFieldID].projectUnitCustomFieldSettingOptions = arrayMove(
        editingData[optionFieldID].projectUnitCustomFieldSettingOptions,
        oldIndex,
        newIndex,
      );
    } else {
      editingData[optionFieldID].projectBookingCustomFieldOptions = arrayMove(
        editingData[optionFieldID].projectBookingCustomFieldOptions,
        oldIndex,
        newIndex,
      );
    }
    this.sortDataByIndexOptions(editingData, optionFieldID);
  };
  getSortedData = (data) => {
    if (data) {
      return data.sort((a, b) => a.position - b.position);
    }
    return [];
  }
  getOptionsData = (data, fieldID) => {
    const { type } = this.props;
    if (data &&
      data.length > 0 &&
      data[fieldID]
    ) {
      if (type === 'unit' && data[fieldID].projectUnitCustomFieldSettingOptions) return data[fieldID].projectUnitCustomFieldSettingOptions;
      if (type === 'booking' && data[fieldID].projectBookingCustomFieldOptions) return data[fieldID].projectBookingCustomFieldOptions;
      return data[fieldID].projectBookingCustomFieldOptions;
    }
    return [];
  }
  sortDataByIndexOptions = (nd, fieldID) => {
    const { type } = this.props;
    const newData = nd;
    if (type === 'unit') {
      newData[fieldID].projectUnitCustomFieldSettingOptions.forEach((d, index) => {
        newData[fieldID].projectUnitCustomFieldSettingOptions[index].position = index + 1;
      });
    } else {
      newData[fieldID].projectBookingCustomFieldOptions.forEach((d, index) => {
        newData[fieldID].projectBookingCustomFieldOptions[index].position = index + 1;
      });
    }
    this.setState({ editingData: newData });
  }
  sortDataByIndex = (nd) => {
    const { isDefault, data, defaultData } = this.state;
    const newData = nd;
    newData.forEach((d, index) => {
      newData[index].position = index + 1;
    });
    const allData = data.concat(defaultData);
    if (isDefault) {
      this.setState({ defaultData: newData }, () => this.props.onChangeData(allData));
    } else {
      this.setState({ data: newData, editingData: this.handleOnDeepCopyData(newData) }, () => this.props.onChangeData(allData));
    }
  }
  handleOnDeepCopyData = d => JSON.parse(JSON.stringify(d));
  handleOnChange = (e, d, index, isDefault = false) => {
    const { data, defaultData } = this.state;
    const newData = isDefault ? defaultData : data;
    if (d && d.validationFormat && typeof d.validationFormat === 'object' && !Array.isArray(d.validationFormat)) {
      newData[index].formatName = d.validationFormat.value;
      newData[index].regex = d.validationFormat.regex;
      newData[index].errorMessage = checkRegex(newData[index].defaultValue, newData[index]).message;
    }
    if (d.hasOwnProperty('checked')) {
      newData[index][d.name] = d.checked;
      
      // Mutual exclusion: If Required is checked, uncheck Lock/Disabled (and vice versa)
      if (d.name === 'isRequired' && d.checked) {
        newData[index].isDisabled = false;
      } else if (d.name === 'isDisabled' && d.checked) {
        newData[index].isRequired = false;
      }
    } else {
      newData[index][d.name] = d.value;
    }
    if (d.name === 'type') {
      newData[index].defaultValue = '';
    }
    if (!newData[index].isDefault && d.name === 'label' && d.value !== '') {
      newData[index].name = d.value.toLowerCase().replace(/[^a-zA-Z0-9]+/g, '_').replace(/_+$/, '');
    }
    if (d.name === 'defaultValue') {
      if (Array.isArray(data.value)) {
        newData[index].defaultValue = d.value.filter(v => v !== '').join();
      } else {
        newData[index].errorMessage = checkRegex(d.value, newData[index]).message;
      }
    }
    this.setState({ isDefault }, () => this.sortOptionsDataByIndex(newData));
  }
  handleOnAdd = (e) => {
    e.preventDefault();
    const { type } = this.props;
    const { data, addType } = this.state;
    const lastID = data.length;
    const newItem = Object.assign({}, type === 'unit' ? CustomField.defaultUnitValues : CustomField.defaultBookingValues);
    if (type === 'unit') {
      newItem.projectUnitCustomFieldSettingOptions = [];
    } else {
      newItem.projectBookingCustomFieldOptions = [];
    }
    newItem.type = addType;
    if (lastID > 0) {
      newItem.position = data[lastID - 1].position;
    }
    data.push(newItem);
    this.setState({ isDefault: false }, () => this.sortDataByIndex(data));
  }
  handleOnAddChange = (e, d) => {
    this.setState({ addType: d.value });
  }
  handleOnEdit = (e, index, isDefault = false) => {
    e.preventDefault();
    const { data, defaultData } = this.state;
    const newData = isDefault ? defaultData : data;
    if (
      newData[index].isEditing &&
      newData[index].id === '' && (
      newData[index].name === '' ||
      newData[index].label === '')
    ) {
      newData.splice(index, 1);
    } else {
      newData[index].isEditing =
      !newData[index].isEditing;
    }
    this.setState({ isDefault }, () => this.sortDataByIndex(newData));
  }
  // Options
  sortOptionsDataByIndex = (nd, isCustomFieldOpts = false) => {
    const { type } = this.props;
    const { isDefault, data, defaultData, editingData } = this.state;
    const newData = nd;
    newData.forEach((d, index) => {
      if (type === 'unit' && d.projectUnitCustomFieldSettingOptions) {
        d.projectUnitCustomFieldSettingOptions.forEach((o, idx) => {
          newData[index].projectUnitCustomFieldSettingOptions[idx].position = idx + 1;
        });
      } else if (d.projectBookingCustomFieldOptions) {
        d.projectBookingCustomFieldOptions.forEach((o, idx) => {
          newData[index].projectBookingCustomFieldOptions[idx].position = idx + 1;
        });
      }
    });
    const currentData = isCustomFieldOpts ? editingData : data;
    const allData = currentData.concat(defaultData);
    if (isDefault) {
      this.setState({ defaultData: newData }, () => this.props.onChangeData(allData));
    } else if (isCustomFieldOpts) {
      this.setState({ editingData: newData });
    } else {
      this.setState({ data: newData, editingData: this.handleOnDeepCopyData(newData) }, () => this.props.onChangeData(allData));
    }
  }
  handleOptionsOnChange = (e, d, index) => {
    const { type } = this.props;
    const { editingData, optionFieldID } = this.state;
    if (type === 'unit') {
      if (e.target.name === 'dataValue') {
        editingData[optionFieldID].projectUnitCustomFieldSettingOptions[index][e.target.name] = e.target.value;
      } else if (d.hasOwnProperty('checked')) {
        editingData[optionFieldID].projectUnitCustomFieldSettingOptions[index][d.name] = d.checked;
      } else {
        editingData[optionFieldID].projectUnitCustomFieldSettingOptions[index][d.name] = d.value;
      }
      if (e.target.name === 'title' && d.value !== '') {
        editingData[optionFieldID].projectUnitCustomFieldSettingOptions[index].value = d.value.toLowerCase().replace(/[^a-zA-Z0-9]+/g, '_').replace(/_+$/, '');
      }
    } else if (e.target.name === 'dataValue') {
      editingData[optionFieldID].projectBookingCustomFieldOptions[index][e.target.name] = e.target.value;
    } else if (d.hasOwnProperty('checked')) {
      editingData[optionFieldID].projectBookingCustomFieldOptions[index][d.name] = d.checked;
    } else {
      editingData[optionFieldID].projectBookingCustomFieldOptions[index][d.name] = d.value;
    }
    this.sortOptionsDataByIndex(editingData, true);
  }
  handleOptionsOnAdd = (e) => {
    e.preventDefault();
    const { type } = this.props;
    const { editingData, optionFieldID } = this.state;
    let options = [];
    if (type === 'unit') {
      if (editingData[optionFieldID].projectUnitCustomFieldSettingOptions) {
        options = editingData[optionFieldID].projectUnitCustomFieldSettingOptions;
      }
      const lastID = options.length;
      const newItem = Object.assign({}, CustomField.defaultUnitOptionValues);
      newItem.projectUnitCustomFieldSettingID = editingData[optionFieldID].id;
      if (lastID > 0) {
        newItem.position = options[lastID - 1].position;
      }
      options.push(newItem);
      editingData[optionFieldID].projectUnitCustomFieldSettingOptions = options;
    } else {
      if (editingData[optionFieldID] && editingData[optionFieldID].projectBookingCustomFieldOptions) {
        options = editingData[optionFieldID].projectBookingCustomFieldOptions;
      }
      const lastID = options.length;
      const newItem = Object.assign({}, CustomField.defaultBookingOptionValues);
      newItem.projectBookingCustomFieldID = editingData[optionFieldID].id;
      if (lastID > 0) {
        newItem.position = options[lastID - 1].position;
      }
      options.push(newItem);
      editingData[optionFieldID].projectBookingCustomFieldOptions = options;
    }
    this.sortOptionsDataByIndex(editingData, true);
  }
  handleOptionsOnEdit = (e, index) => {
    const { type } = this.props;
    const { optionFieldID } = this.state;
    e.preventDefault();
    const newData = this.state.editingData;
    if (type === 'unit') {
      if (
        newData[optionFieldID].projectUnitCustomFieldSettingOptions[index].isEditing &&
        newData[optionFieldID].projectUnitCustomFieldSettingOptions[index].id === '' && (
        newData[optionFieldID].projectUnitCustomFieldSettingOptions[index].title === '' ||
        newData[optionFieldID].projectUnitCustomFieldSettingOptions[index].value === '')
      ) {
        newData[optionFieldID].projectUnitCustomFieldSettingOptions.splice(index, 1);
      } else {
        newData[optionFieldID].projectUnitCustomFieldSettingOptions[index].isEditing =
        !newData[optionFieldID].projectUnitCustomFieldSettingOptions[index].isEditing;
      }
    } else if (
      newData[optionFieldID].projectBookingCustomFieldOptions[index].isEditing &&
      newData[optionFieldID].projectBookingCustomFieldOptions[index].id === '' && (
      newData[optionFieldID].projectBookingCustomFieldOptions[index].title === '' ||
      newData[optionFieldID].projectBookingCustomFieldOptions[index].value === '')
    ) {
      newData[optionFieldID].projectBookingCustomFieldOptions.splice(index, 1);
    } else {
      newData[optionFieldID].projectBookingCustomFieldOptions[index].isEditing =
      !newData[optionFieldID].projectBookingCustomFieldOptions[index].isEditing;
    }
    this.sortOptionsDataByIndex(newData, true);
  }
  handleOnClickOptions = (e, optionFieldID) => {
    e.preventDefault();
    this.setState({
      optionFieldID,
      optionModalIsOpen: true,
    });
  }
  handleOnClickName = (e, index) => {
    const { data } = this.state;
    e.preventDefault();
    data[index].showName = !data[index].showName;
    this.sortOptionsDataByIndex(data);
  }
  handleOptionsModalSave = () => {
    const { type } = this.props;
    const { editingData, optionFieldID, defaultData } = this.state;
    if (type === 'unit' && editingData[optionFieldID].projectUnitCustomFieldSettingOptions &&
      editingData[optionFieldID].projectUnitCustomFieldSettingOptions.length > 0) {
      editingData[optionFieldID].projectUnitCustomFieldSettingOptions.forEach((d, idx) => {
        editingData[optionFieldID].projectUnitCustomFieldSettingOptions[idx].isEditing = false;
      });
    } else if (type === 'booking' && editingData[optionFieldID].projectBookingCustomFieldOptions &&
      editingData[optionFieldID].projectBookingCustomFieldOptions.length > 0) {
      editingData[optionFieldID].projectBookingCustomFieldOptions.forEach((d, idx) => {
        editingData[optionFieldID].projectBookingCustomFieldOptions[idx].isEditing = false;
      });
    }
    const allData = type === 'unit' ? editingData : editingData.concat(defaultData);
    this.setState({
      editingData,
      data: this.handleOnDeepCopyData(editingData),
      optionModalIsOpen: false,
    }, () => this.props.onChangeData(allData));
  }
  handleOptionsModalCancel = () => this.setState({ editingData: this.handleOnDeepCopyData(this.state.data), optionModalIsOpen: false });
  handleOrderItems = (e, { name }) => {
    let data = this.state.data;
    switch (name) {
      case 'alphabetically':
        data = data.sort((a, b) => a.label.localeCompare(b.label));
        break;
      case 'newest':
        data = data.sort((a, b) => {
          const aTime = moment(b.createdAt).valueOf().toString();
          const bTime = moment(a.createdAt).valueOf().toString();
          return aTime.localeCompare(bTime);
        });
        break;
      case 'oldest':
        data = data.sort((b, a) => {
          const aTime = moment(b.createdAt).valueOf().toString();
          const bTime = moment(a.createdAt).valueOf().toString();
          return aTime.localeCompare(bTime);
        });
        break;
      default:
        return;
    }
    this.sortDataByIndex(data);
  }
  checkOptionAddButtonDisabled = () => {
    const { type } = this.props;
    const { editingData, optionFieldID, optionModalIsOpen } = this.state;
    if (optionModalIsOpen) {
      const options = type === 'unit' ? editingData[optionFieldID].projectUnitCustomFieldSettingOptions : editingData[optionFieldID].projectBookingCustomFieldOptions;
      if (options && options.filter(d => d.title === '' || d.value === '').length > 0) {
        return true;
      }
    }
    return false;
  }
  checkOptionSaveButtonDisabled = () => {
    const { type } = this.props;
    const { editingData, optionFieldID, optionModalIsOpen } = this.state;
    if (optionModalIsOpen) {
      const options = type === 'unit' ? editingData[optionFieldID].projectUnitCustomFieldSettingOptions : editingData[optionFieldID].projectBookingCustomFieldOptions;
      if (!options || options.length === 0) {
        return false;
      }
      return options.filter(d => !d.isDeleted && (d.title === '' || d.value === '')).length > 0;
    }
    return false;
  }
  checkAddButtonDisabled = () => {
    const { data } = this.state;
    if (data.filter(d => d.name === '' || d.label === '').length > 0) {
      return true;
    }
    return false;
  }
  hasCompanyHimsEnabled = () => {
    const { appConfig } = this.props;
    return appConfig && appConfig.data && appConfig.data.himsIsEnabled;
  }
  hasVAEnabled = () => {
    const { appConfig } = this.props;
    return appConfig && appConfig.data && appConfig.data.settings && appConfig.data.settings.virtualAccountIsEnabled;
  }
  renderUnitDefaultFields = () => {
    const { type, t } = this.props;
    const { defaultData } = this.state;
    const UNIT_DEFAULT_FIELDS = [
      { title: 'HIMS Unit Fields', fields: HIMS_UNIT_FIELDS, enabled: this.hasCompanyHimsEnabled() },
      { title: 'Virtual Account Fields', fields: VA_UNIT_FIELDS, enabled: this.hasVAEnabled() },
    ];
    return (
      <Grid.Column width={16}>
        {UNIT_DEFAULT_FIELDS.map((f, idx) => f.enabled && (
          <Grid.Row style={idx > 0 ? { paddingTop: '2em' } : null}>
            <Grid.Column width={16}>
              <Divider horizontal>{t(f.title)}</Divider>
              <SortableFieldList
                addDisabled={this.checkAddButtonDisabled()}
                helperClass="PBCFSetup isSorting"
                items={this.getSortedData(defaultData.filter(d => f.fields.some(ff => d.name === ff)))}
                lockAxis="y"
                lockToContainerEdges
                onChange={(e, d, i) => this.handleOnChange(e, d, i, true)}
                onEdit={(e, i) => this.handleOnEdit(e, i, true)}
                onSortEnd={v => this.onSortEnd(v, true)}
                useDragHandle
                isDefault
                type={type}
                t={t}
                startIndex={idx === 0 ? 0 : UNIT_DEFAULT_FIELDS[idx - 1].fields.length}
              />
            </Grid.Column>
          </Grid.Row>
      ))}
      </Grid.Column>
    );
  }
  render() {
    const { type, t } = this.props;
    const {
      data,
      editingData,
      optionFieldID,
      optionModalIsOpen,
      defaultData,
    } = this.state;
    return (
      <Grid className="PBCFSetup">
        {
          type === 'booking' && (
            <Grid.Row>
              <Grid.Column width={16}>
                <Divider horizontal>{t('Purchaser Default Fields (Fixed)')}</Divider>
                <SortableFieldList
                  addDisabled={this.checkAddButtonDisabled()}
                  helperClass="PBCFSetup isSorting"
                  items={this.getSortedData(defaultData).map(d => ({ ...d }))}
                  lockAxis="y"
                  lockToContainerEdges
                  onChange={(e, d, i) => this.handleOnChange(e, d, i, true)}
                  onEdit={(e, i) => this.handleOnEdit(e, i, true)}
                  onSortEnd={v => this.onSortEnd(v, true)}
                  useDragHandle
                  isDefault
                  type={type}
                  t={t}
                />
              </Grid.Column>
            </Grid.Row>
          )
        }
        {type === 'unit' && this.renderUnitDefaultFields()}
        <Grid.Row>
          <Grid.Column width={16}>
            <Divider horizontal>{t(type === 'booking' ? 'Custom Booking Fields' : 'Unit Custom Fields')}</Divider>
            <CustomFieldOptionModal
              addDisabled={this.checkOptionAddButtonDisabled()}
              data={this.getSortedData(this.getOptionsData(editingData, optionFieldID))}
              onAdd={this.handleOptionsOnAdd}
              onChange={this.handleOptionsOnChange}
              onEdit={this.handleOptionsOnEdit}
              onSave={this.handleOptionsModalSave}
              onCancel={this.handleOptionsModalCancel}
              onSortEnd={this.onSortOptionsEnd}
              open={optionModalIsOpen}
              saveDisabled={this.checkOptionSaveButtonDisabled()}
              t={t}
            />
            <Grid>
              <Grid.Row>
                <Grid.Column width={16} textAlign="right">
                  <Menu
                    text
                    compact
                    size="tiny"
                    secondary
                  >
                    {
                      sortItems.map((item, key) => (
                        <Menu.Item
                          key={key}
                          name={item.name}
                          onClick={this.handleOrderItems}
                        >{t(item.text, { ns: TRANSLATION_FILES.translation })}</Menu.Item>
                      ))
                    }
                  </Menu>
                </Grid.Column>
              </Grid.Row>
            </Grid>
            <SortableFieldList
              addDisabled={this.checkAddButtonDisabled()}
              helperClass="PBCFSetup isSorting"
              items={this.getSortedData(data)}
              lockAxis="y"
              lockToContainerEdges
              onAdd={this.handleOnAdd}
              onAddChange={this.handleOnAddChange}
              onChange={this.handleOnChange}
              onClickOptions={this.handleOnClickOptions}
              onClickName={this.handleOnClickName}
              onEdit={this.handleOnEdit}
              onSortEnd={v => this.onSortEnd(v, false)}
              useDragHandle
              type={type}
              t={t}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}

export default withTranslation(namespaces)(connect(CustomField.mapStateToProps)(CustomField));



// WEBPACK FOOTER //
// ./src/components/CustomField.jsx